import React, { useState } from "react";
import { Modal, Upload, Input, Button, Table, message, Spin } from "antd";
import { UploadOutlined, FileTextOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import * as Constants from "../CustomerComponents/Constants";
import ENVIRONMENT from "../../../environments";

const { Dragger } = Upload;

const BulkUploadModal = ({ visible, onCancel, onSubmit }) => {
  const [fileList, setFileList] = useState([]);
  const [uploadName, setUploadName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null); // Array of errors if any

  const [isPreviewHovered, setIsPreviewHovered] = useState(false);

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const resetState = () => {
    setFileList([]);
    setUploadName("");
    setErrors(null);
    setLoading(false);
  };

  const validateFile = (file) => {
    const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
    if (!isCsv) {
      message.error("Only .csv formats are allowed.");
      return false;
    }
    return true;
  };

  const uploadProps = {
    ...Constants.upload_props,
    multiple: false,
    accept: ".csv",
    beforeUpload: (file) => {
      return validateFile(file);
    },
    fileList: fileList,
    showUploadList: false, // Hide default upload list
    onChange(info) {
      setFileList([info.file]);

      const { status } = info.file;
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        if (info.file.error && info.file.error.code && info.file.error.code.includes('Credentials')) {
          message.error('Your session has expired. Please reload the page.');
        } else {
          message.error(`${info.file.name} file upload failed.`);
        }
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const handleSubmit = async () => {
    if (fileList.length === 0) {
      message.error("Please upload a CSV file.");
      return;
    }
    if (!uploadName.trim()) {
      message.error("Please enter a bulk upload name.");
      return;
    }

    setLoading(true);
    try {
      // Expect onSubmit to return { success: boolean, errors: [] }
      const result = await onSubmit(fileList[0].originFileObj, uploadName);
      if (result.success) {
        resetState();
      } else if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        message.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred during upload.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Error",
      dataIndex: "message",
      key: "message",
    },
  ];

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      width={600}
      footer={null}
      centered
      destroyOnClose
    >
      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h4 className="manrope f-20 w-500 black-00" style={{ margin: 0 }}>
            {errors ? "Upload Errors" : "Upload Multiple Products"}
          </h4>
          {!errors && (
            <a href="https://d48f7equ64qjl.cloudfront.net/static_files/ai-bulk-request-template.csv" className="manrope f-14" style={{ color: "#1890ff" }}>
              Download CSV template
            </a>
          )}
        </div>

        {errors ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <p className="manrope f-14">The uploaded CSV contains errors. Please fix them and re-upload.</p>
              <Table
                dataSource={errors}
                columns={columns}
                pagination={false}
                scroll={{ y: 300 }}
                rowKey="row"
                size="small"
                bordered
              />
            </div>
            <div className="justify-in-end">
                <Button onClick={resetState} className="modal-okay square font-14">
                    Re-upload
                </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 24 }}>
              {fileList.length === 0 ? (
                <Dragger
                  {...uploadProps}
                  style={{
                    border: "1px dashed #595959",
                    background: "white",
                    borderRadius: 8,
                    padding: "32px 24px",
                    textAlign: "center",
                    minHeight: "140px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <div className="manrope" style={{ width: "100%" }}>
                    <div style={{ color: "black", fontSize: "16px", marginBottom: "12px", fontWeight: "400" }}>Drag & Drop or</div>
                    <Button icon={<UploadOutlined />} className="upload-photo-btn">Upload CSV</Button>
                    <div style={{ fontSize: 12, color: "#595959", marginTop: "12px", lineHeight: "1.4" }}>
                      Please use the CSV template provided.
                    </div>
                    <div style={{ fontSize: 12, color: "#595959", lineHeight: "1.4" }}>
                      Supported formats: CSV
                    </div>
                  </div>
                </Dragger>
              ) : (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {fileList.map((file) => (
                    <div
                      key={file.uid}
                      style={{
                        position: 'relative',
                        width: '132px',
                        height: '132px',
                        border: '1px solid #D9D9D9',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={() => setIsPreviewHovered(true)}
                      onMouseLeave={() => setIsPreviewHovered(false)}
                    >
                      {file.status === "uploading" ? (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%',
                          backgroundColor: '#f5f5f5'
                        }}>
                          <Spin indicator={<LoadingOutlined spin />} style={{color: '#8C8C8C'}} size="large" />
                        </div>
                      ) : (
                        <>
                          <div style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                             <FileTextOutlined style={{ fontSize: '32px', color: '#8C8C8C', marginBottom: '8px' }} />
                             <span className="manrope f-12" style={{ padding: '0 8px', textAlign: 'center', wordBreak: 'break-word' }}>
                               {file.name.length > 20 
                                 ? file.name.substring(0, 12) + '...' + file.name.substring(file.name.length - 8) 
                                 : file.name}
                             </span>
                          </div>
                          <Button
                            onClick={() => {
                              setFileList([]);
                            }}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              left: '8px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#000000',
                              borderRadius: '4px',
                              border: 'none',
                              opacity: isPreviewHovered ? 1 : 0,
                              transition: 'opacity 120ms ease-in-out'
                            }}
                          >
                            <DeleteOutlined style={{ color: 'white', fontSize: '12px' }} />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label className="manrope f-14 w-400 black-00" style={{ display: "block", marginBottom: 8 }}>
                <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>Bulk upload name
              </label>
              <Input
                placeholder="Enter name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                className="manrope f-14"
                style={{ height: 40 }}
              />
            </div>

            <div className="justify-in-end">
              <Button
                onClick={handleCancel}
                className="modal-okay-gray square font-14"
                style={{ marginRight: 10 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={loading}
                disabled={fileList.length > 0 && fileList[0].status === 'uploading'}
                className="modal-okay square font-14"
                type="primary"
                style={{ backgroundColor: "#1890ff", borderColor: "#1890ff" }} // Explicit color to match screenshot blue
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkUploadModal;
